'use client';

import { isValid } from 'date-fns';
import { Card } from '@/components/ui/card';
import {
  Edit,
  CalendarIcon,
  Clock,
  MapPin,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDateForUser, formatTimeForUser, fromUTC, toDateInputFormat } from '@/utils/formatters';
import { fitsAvailability, fitsTournamentSchedule } from '@/utils/availability-fit';
import { useState } from 'react';
import { toast } from '@/components/ui/use-toast';
import {
  TO_BE_DEFINED_LABEL,
  type Availability,
  type Match,
  type MatchResult,
  type Player,
} from '@/types/tournament.model';
import {
  setMatchResult,
  editMatchResult,
  updateMatchDateTime,
  deleteMatch,
} from '@/api/tournament/tournamentApi';
import { MatchEditModal } from './match/MatchEditModal';
import { MatchDateTimeEditModal } from './match/MatchDateTimeEditModal';
import { MatchDeleteConfirmModal } from './match/MatchDeleteConfirmModal';
import { ForcedReasonBadge } from './match/ForcedReasonBadge';
import { handleGlobalError } from '@/utils/error-handler';
import { PlayerInfoPopover } from '../PlayerInfoPopover';
import { PlayersNamesList } from '../PlayerNamesList';
import type { EloCategory } from '../models/elo.model';
import { AvailabilityStatusIcon } from './AvailabilityStatusIcon';
import { StatusIcon } from './StatusIcon';

export interface MatchCardProps {
  match: Match;
  team1Name: string;
  team2Name: string;
  isFinal?: boolean;
  /** 1-based position of the match inside its group; renders the "Partido N" chip. */
  matchNumber?: number;
  tournamentId: string;
  onResultUpdated?: () => void;
  isEditable?: boolean;
  disabled?: boolean;
  /** When false, result load/edit is blocked because the fixture is not active yet. */
  canLoadResults?: boolean;
  groupTeamCount?: number;
  totalMatchesInGroup?: number;
  team1Object?: Match['team1'];
  team2Object?: Match['team2'];
  team1Availability?: Availability[];
  team2Availability?: Availability[];
  /** Icon copy for side 1 when it isn't a single team yet (dependent match); default copy otherwise. */
  team1AvailabilityMessages?: { ok?: string; notOk?: string };
  /** Same as `team1AvailabilityMessages`, for side 2. */
  team2AvailabilityMessages?: { ok?: string; notOk?: string };
  tournamentScheduleAvailability?: Availability[];
  scheduleConflictMessage?: string;
  /** LONG only: backend-authoritative — the slot was forced because availability didn't match. */
  forcedConflict?: boolean;
  /** LONG only: forced because a team didn't load availability (softer, amber) vs a real clash (red). */
  forcedMissingAvailability?: boolean;
  /** LONG only: backend-authoritative — proposed but not published yet. */
  isProposed?: boolean;
  /** Resolves Elo / category in player popovers and name list from `player.rankings`. */
  sportUid?: string;
  eloCategories?: EloCategory[];
}

export function MatchCard({
  match,
  team1Name,
  team2Name,
  isFinal = false,
  matchNumber,
  tournamentId,
  onResultUpdated,
  isEditable = true,
  disabled = false,
  canLoadResults = true,
  groupTeamCount,
  totalMatchesInGroup,
  team1Object,
  team2Object,
  team1Availability,
  team2Availability,
  team1AvailabilityMessages,
  team2AvailabilityMessages,
  tournamentScheduleAvailability,
  scheduleConflictMessage,
  forcedConflict = false,
  forcedMissingAvailability = false,
  isProposed = false,
  sportUid,
  eloCategories = [],
}: MatchCardProps) {
  const eloContext =
    sportUid != null && sportUid !== ''
      ? { sportUid, eloCategories }
      : undefined;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDateTimeEditModal, setShowDateTimeEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // A team has real players when at least one player has an email (label players only have `name`)
  const teamHasRealPlayers = (players: Partial<Player>[]) =>
    players.length > 0 && players.every((p) => Boolean(p.email));

  const team1HasRealPlayers = teamHasRealPlayers(match.team1?.players ?? []);
  const team2HasRealPlayers = teamHasRealPlayers(match.team2?.players ?? []);

  const hasUndefinedTeams =
    team1Name === TO_BE_DEFINED_LABEL ||
    team2Name === TO_BE_DEFINED_LABEL ||
    team1Name.startsWith('Equipo ') ||
    team2Name.startsWith('Equipo ') ||
    !team1HasRealPlayers ||
    !team2HasRealPlayers;

  const handleSaveResult = async (result: MatchResult) => {
    const isEditingExistingResult = Boolean(match.result?.isValid);
    try {
      setIsSubmitting(true);

      if (isEditingExistingResult) {
        await editMatchResult(tournamentId, match.id, result);
      } else {
        await setMatchResult(tournamentId, match.id, result);
      }

      if (onResultUpdated) {
        onResultUpdated();
      }

      toast({
        title: isEditingExistingResult
          ? 'Resultado editado'
          : 'Resultado cargado',
        description: isEditingExistingResult
          ? 'El resultado del partido ha sido editado correctamente.'
          : 'El resultado del partido ha sido cargado correctamente.',
        variant: 'default',
      });
    } catch (error) {
      console.error('Error al guardar el resultado:', error);
      handleGlobalError(
        error,
        'Error',
        isEditingExistingResult
          ? 'No se pudo editar el resultado. Inténtalo de nuevo.'
          : 'No se pudo cargar el resultado. Inténtalo de nuevo.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClick = () => {
    if (hasUndefinedTeams) {
      toast({
        title: 'No se puede cargar resultado',
        description:
          'No se pueden cargar resultados hasta que ambos equipos estén definidos.',
        variant: 'destructive',
      });
      return;
    }
    setShowEditModal(true);
  };

  const handleDateTimeEditClick = () => {
    setShowDateTimeEditModal(true);
  };

  const handleSaveDateTime = async (startTime: string) => {
    try {
      setIsSubmitting(true);

      await updateMatchDateTime(tournamentId, match.id, startTime);

      if (onResultUpdated) {
        onResultUpdated();
      }

      toast({
        title: 'Fecha y horario actualizados',
        description:
          'La fecha y horario del partido han sido actualizados correctamente.',
        variant: 'default',
      });

      setShowDateTimeEditModal(false);
    } catch (error) {
      console.error('Error al actualizar fecha y horario:', error);
      handleGlobalError(
        error,
        'Error',
        'No se pudo actualizar la fecha y horario. Inténtalo de nuevo.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMatch = async () => {
    try {
      setIsSubmitting(true);

      await deleteMatch(match.id);

      if (onResultUpdated) {
        onResultUpdated();
      }

      toast({
        title: 'Partido eliminado',
        description: 'El partido ha sido eliminado correctamente.',
        variant: 'default',
      });

      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error al eliminar el partido:', error);
      handleGlobalError(
        error,
        'Error',
        'No se pudo eliminar el partido. Inténtalo de nuevo.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isMatchFinished = match.result && match.result.isValid;
  const isDoubleWalkover = Boolean(
    match.result?.team1Walkover && match.result?.team2Walkover,
  );
  // Backend semantics: teamXWalkover === teamX did walkover (absence/retirement),
  // meaning the OTHER team is the winner.
  const isTeam1Winner =
    isMatchFinished &&
    !isDoubleWalkover &&
    (match.result?.team2Walkover ||
      (!match.result?.team1Walkover &&
        !match.result?.team2Walkover &&
        (match.result?.team1Score ?? 0) > (match.result?.team2Score ?? 0)));
  const isTeam2Winner =
    isMatchFinished &&
    !isDoubleWalkover &&
    (match.result?.team1Walkover ||
      (!match.result?.team1Walkover &&
        !match.result?.team2Walkover &&
        (match.result?.team2Score ?? 0) > (match.result?.team1Score ?? 0)));

  // Special result detection
  const hasSpecialResult =
    match.result?.team1Walkover ||
    match.result?.team2Walkover;

  const getSpecialResultLabel = () => {
    if (match.result?.team1Walkover && match.result?.team2Walkover)
      return 'Doble walkover (ambos equipos con ausencia/abandono)';
    if (match.result?.team1Walkover)
      return `Ganó ${team2Name} (ausencia/abandono)`;
    if (match.result?.team2Walkover)
      return `Ganó ${team1Name} (ausencia/abandono)`;
    return null;
  };

  // Determine if the delete button should be shown
  // Can only delete if: match has no result, we have group info, and total matches > team count
  const canDeleteMatch =
    !isMatchFinished &&
    groupTeamCount !== undefined &&
    totalMatchesInGroup !== undefined &&
    totalMatchesInGroup > groupTeamCount;

  // Determine if the edit button should be disabled
  const isEditDisabled = disabled || hasUndefinedTeams || !canLoadResults;

  // Tooltip shown on the disabled result buttons when the fixture is not active
  const resultsLockedTitle = !canLoadResults
    ? 'Activá el fixture para cargar resultados'
    : undefined;

  const getPlayerInitials = (teamName: string, playerIndex: number) => {
    const players = teamName.split(' / ');
    if (players[playerIndex]) {
      const names = players[playerIndex].trim().split(' ');
      if (names.length >= 2) {
        return (
          names[0].charAt(0).toUpperCase() +
          names[names.length - 1].charAt(0).toUpperCase()
        );
      }
      return (
        names[0].charAt(0).toUpperCase() +
        (names[0].charAt(1) || '').toUpperCase()
      );
    }
    return playerIndex === 0 ? 'P1' : 'P2';
  };

  // Get player profile image from team object
  const getPlayerImage = (
    teamObject: Match['team1'] | Match['team2'] | undefined,
    playerIndex: number,
  ): string | undefined => {
    if (!teamObject || typeof teamObject === 'string') return undefined;
    return teamObject.players?.[playerIndex]?.profileImageUrl;
  };

  // Teams with no real players are placeholders: a dependent match waiting on another result, or
  // a bracket slot whose only "player" is the smuggled qualifier label. Their name is a label
  // ("Ganador Partido 1", "2° Grupo B"), so initials derived from it read as nonsense ("GP").
  const placeholderAvatar = (
    <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-slate-300 text-sm font-semibold text-white">
      ?
    </div>
  );

  // Get player object from team
  const getPlayer = (
    teamObject: Match['team1'] | Match['team2'] | undefined,
    playerIndex: number,
  ) => {
    if (!teamObject || typeof teamObject === 'string') return undefined;
    return teamObject.players?.[playerIndex];
  };

  // The match's date and time in the user's local time, as `YYYY-MM-DD` and `HH:mm` — the shape
  // `Availability.date` and `timeRange` are compared in. Empty when there's no valid start date,
  // which the shared checks below read as "unknown" (no icon).
  // `toDateInputFormat` throws on an invalid date, hence the guard. It parses with `fromUTC`
  // (`parseISO`), so the guard has to as well: `new Date` accepts strings `parseISO` rejects.
  const start = match.startDate ? fromUTC(match.startDate) : null;
  const hasValidStart = start !== null && isValid(start);
  const matchDate = hasValidStart ? toDateInputFormat(start) : '';
  const matchTime = hasValidStart ? formatTimeForUser(match.startDate) : '';

  const team1AvailabilityOk = fitsAvailability(team1Availability, matchDate, matchTime);
  const team2AvailabilityOk = fitsAvailability(team2Availability, matchDate, matchTime);
  const tournamentScheduleOk = fitsTournamentSchedule(
    tournamentScheduleAvailability,
    matchDate,
    matchTime,
  );

  return (
    <>
      <Card
        className={`flex h-full w-full max-w-sm flex-col overflow-hidden bg-white shadow-lg ${forcedConflict && !forcedMissingAvailability ? 'border-2 border-red-500' : forcedConflict && forcedMissingAvailability ? 'border-2 border-amber-500' : scheduleConflictMessage ? 'border-2 border-red-500' : 'border-0'} ${isFinal ? 'ring-2 ring-yellow-400' : ''} ${disabled ? 'opacity-60' : ''}`}
      >
        {forcedConflict && !forcedMissingAvailability && (
          <div className="flex items-center gap-2 bg-red-500 px-4 py-2 text-center text-sm font-medium text-white">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>Horario forzado: la disponibilidad no coincidió. Te recomendamos reprogramarlo.</span>
          </div>
        )}
        {forcedConflict && forcedMissingAvailability && (
          <div className="flex items-center gap-2 bg-amber-500 px-4 py-2 text-center text-sm font-medium text-white">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>Falta disponibilidad: un equipo no cargó sus horarios. Podés reprogramarlo si hace falta.</span>
          </div>
        )}
        {scheduleConflictMessage && !forcedConflict && (
          <div className="bg-red-500 px-4 py-2 text-center text-sm font-normal text-white">
            {scheduleConflictMessage}
          </div>
        )}
        <div className="flex flex-1 flex-col p-4 pb-3">
          <div className="mb-3 flex items-start justify-between">
            <div className="space-y-1">
              {matchNumber !== undefined && (
                <div>
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                    Partido {matchNumber}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CalendarIcon className="h-4 w-4" />
                <span>{formatDateForUser(match.startDate)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="h-4 w-4" />
                <span>{formatTimeForUser(match.startDate)}</span>
              </div>
            </div>

            <div className="flex flex-col items-end gap-1">
              <ForcedReasonBadge
                reason={match.forcedReason}
                matchId={match.id}
              />
              {isMatchFinished ? (
                <span className="inline-flex items-center rounded-full bg-black px-3 py-1 text-xs font-medium text-white">
                  Finalizado
                </span>
              ) : (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                  Pendiente
                </span>
              )}
              {isProposed && !isMatchFinished && (
                <span className="inline-flex items-center rounded-full bg-slate-200 px-3 py-1 text-xs font-medium text-slate-700">
                  Propuesto
                </span>
              )}

              {isEditable && !isMatchFinished && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-1 text-xs text-gray-600 hover:text-gray-900"
                  onClick={handleDateTimeEditClick}
                  disabled={disabled || isSubmitting}
                >
                  <Edit className="mr-1 h-3 w-3" />
                  Editar fecha
                </Button>
              )}
            </div>
          </div>

          {match.club && (
            <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
              <MapPin className="h-4 w-4" />
              <span>
                {match.club.name} - {match.location}
              </span>
            </div>
          )}

          <div className="mb-4 space-y-3">
            {/* Team 1 */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex -space-x-2">
                {!team1HasRealPlayers ? (
                  placeholderAvatar
                ) : (
                  <>
                {(() => {
                  const player1 = getPlayer(match.team1, 0);
                  const avatar = getPlayerImage(match.team1, 0) ? (
                    <img
                      src={getPlayerImage(match.team1, 0)}
                      alt=""
                      className={`h-8 w-8 rounded-full border-2 border-white object-cover ${isTeam1Winner ? 'ring-2 ring-green-500' : ''}`}
                    />
                  ) : (
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full border-2 border-white text-xs font-semibold text-white ${isTeam1Winner ? 'bg-green-500' : 'bg-slate-600'}`}
                    >
                      {getPlayerInitials(team1Name, 0)}
                    </div>
                  );
                  return player1 ? (
                    <PlayerInfoPopover
                      player={player1}
                      {...(eloContext ?? {})}
                    >
                      {avatar}
                    </PlayerInfoPopover>
                  ) : (
                    avatar
                  );
                })()}
                {(() => {
                  const player2 = getPlayer(match.team1, 1);
                  const avatar = getPlayerImage(match.team1, 1) ? (
                    <img
                      src={getPlayerImage(match.team1, 1)}
                      alt=""
                      className={`h-8 w-8 rounded-full border-2 border-white object-cover ${isTeam1Winner ? 'ring-2 ring-green-400' : ''}`}
                    />
                  ) : (
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full border-2 border-white text-xs font-semibold text-white ${isTeam1Winner ? 'bg-green-400' : 'bg-slate-500'}`}
                    >
                      {getPlayerInitials(team1Name, 1)}
                    </div>
                  );
                  return player2 ? (
                    <PlayerInfoPopover
                      player={player2}
                      {...(eloContext ?? {})}
                    >
                      {avatar}
                    </PlayerInfoPopover>
                  ) : (
                    avatar
                  );
                })()}
                  </>
                )}
                </div>
                <div className="min-w-0">
                  <PlayersNamesList
                    players={
                      match.team1 && typeof match.team1 !== 'string'
                        ? match.team1.players
                        : undefined
                    }
                    fallbackName={team1Name}
                    stackOnMobile
                  />
                </div>
              </div>
              {team1AvailabilityOk !== null && (
                <div className="flex items-center gap-1">
                  {tournamentScheduleOk === false ? (
                    <StatusIcon
                      variant="error"
                      ariaLabel="Horario fuera de horarios del torneo"
                        message="El horario del partido está fuera de los bloques de horarios disponibles del torneo."
                    />
                  ) : (
                    <AvailabilityStatusIcon ok={team1AvailabilityOk} messages={team1AvailabilityMessages} />
                  )}
                </div>
              )}
            </div>

            {/* Team 2 */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex -space-x-2">
                {!team2HasRealPlayers ? (
                  placeholderAvatar
                ) : (
                  <>
                {(() => {
                  const player1 = getPlayer(match.team2, 0);
                  const avatar = getPlayerImage(match.team2, 0) ? (
                    <img
                      src={getPlayerImage(match.team2, 0)}
                      alt=""
                      className={`h-8 w-8 rounded-full border-2 border-white object-cover ${isTeam2Winner ? 'ring-2 ring-green-500' : ''}`}
                    />
                  ) : (
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full border-2 border-white text-xs font-semibold text-white ${isTeam2Winner ? 'bg-green-500' : 'bg-slate-600'}`}
                    >
                      {getPlayerInitials(team2Name, 0)}
                    </div>
                  );
                  return player1 ? (
                    <PlayerInfoPopover
                      player={player1}
                      {...(eloContext ?? {})}
                    >
                      {avatar}
                    </PlayerInfoPopover>
                  ) : (
                    avatar
                  );
                })()}
                {(() => {
                  const player2 = getPlayer(match.team2, 1);
                  const avatar = getPlayerImage(match.team2, 1) ? (
                    <img
                      src={getPlayerImage(match.team2, 1)}
                      alt=""
                      className={`h-8 w-8 rounded-full border-2 border-white object-cover ${isTeam2Winner ? 'ring-2 ring-green-400' : ''}`}
                    />
                  ) : (
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full border-2 border-white text-xs font-semibold text-white ${isTeam2Winner ? 'bg-green-400' : 'bg-slate-500'}`}
                    >
                      {getPlayerInitials(team2Name, 1)}
                    </div>
                  );
                  return player2 ? (
                    <PlayerInfoPopover
                      player={player2}
                      {...(eloContext ?? {})}
                    >
                      {avatar}
                    </PlayerInfoPopover>
                  ) : (
                    avatar
                  );
                })()}
                  </>
                )}
                </div>
                <div className="min-w-0">
                  <PlayersNamesList
                    players={
                      match.team2 && typeof match.team2 !== 'string'
                        ? match.team2.players
                        : undefined
                    }
                    fallbackName={team2Name}
                    stackOnMobile
                  />
                </div>
              </div>
              {team2AvailabilityOk !== null && (
                <div className="flex items-center gap-1">
                  {tournamentScheduleOk === false ? (
                    <StatusIcon
                      variant="error"
                      ariaLabel="Horario fuera de horarios del torneo"
                        message="El horario del partido está fuera de los bloques de horarios disponibles del torneo."
                    />
                  ) : (
                    <AvailabilityStatusIcon ok={team2AvailabilityOk} messages={team2AvailabilityMessages} />
                  )}
                </div>
              )}
            </div>
          </div>

          {isMatchFinished && match.result?.setScores && (
            <div className="mb-4">
              <div className="mb-3 text-center text-sm font-medium text-gray-700">
                Sets
              </div>
              <div className="space-y-2">
                {match.result.setScores.map((set, index) => {
                  const isSet1Winner = set.team1Games > set.team2Games;
                  const isSet2Winner = set.team2Games > set.team1Games;

                  return (
                    <div
                      key={index}
                      className="flex items-center justify-center gap-4 text-sm"
                    >
                      <span className="w-6 text-left font-medium text-gray-600">
                        S{index + 1}
                      </span>
                      <div className="flex items-center gap-2">
                        <span
                          className={`flex h-6 w-6 items-center justify-center rounded text-xs font-semibold ${isSet1Winner ? 'bg-green-100 text-green-800' : 'text-gray-900'}`}
                        >
                          {set.team1Games}
                        </span>
                        <span className="text-xs text-gray-400">-</span>
                        <span
                          className={`flex h-6 w-6 items-center justify-center rounded text-xs font-semibold ${isSet2Winner ? 'bg-green-100 text-green-800' : 'text-gray-900'}`}
                        >
                          {set.team2Games}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-auto flex flex-col gap-3">
            {hasSpecialResult && (
              <div className="flex items-center justify-start rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                <AlertTriangle className="mr-2 h-4 w-4 flex-shrink-0 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">
                  {getSpecialResultLabel()}
                </span>
              </div>
            )}

            {isEditable && !isMatchFinished && !hasUndefinedTeams && (
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-gray-200 bg-transparent text-sm font-medium hover:bg-gray-50"
                  onClick={handleEditClick}
                  disabled={isEditDisabled || isSubmitting}
                  title={resultsLockedTitle}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  Cargar resultado
                </Button>
                {canDeleteMatch && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-red-200 bg-transparent text-sm font-medium text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={() => setShowDeleteModal(true)}
                    disabled={disabled || isSubmitting}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Eliminar
                  </Button>
                )}
              </div>
            )}

            {isEditable && isMatchFinished && (
              <Button
                variant="outline"
                size="sm"
                className="w-full border-gray-200 bg-transparent text-sm font-medium hover:bg-gray-50"
                onClick={handleEditClick}
                disabled={isEditDisabled || isSubmitting}
                title={resultsLockedTitle}
              >
                <Edit className="mr-2 h-4 w-4" />
                Editar resultado
              </Button>
            )}
          </div>
        </div>
      </Card>

      <MatchEditModal
        match={match}
        team1Name={team1Name}
        team2Name={team2Name}
        open={showEditModal}
        onOpenChange={setShowEditModal}
        onSave={handleSaveResult}
        isSubmitting={isSubmitting}
      />

      <MatchDateTimeEditModal
        match={match}
        team1Name={team1Name}
        team2Name={team2Name}
        open={showDateTimeEditModal}
        onOpenChange={setShowDateTimeEditModal}
        onSave={handleSaveDateTime}
        isSubmitting={isSubmitting}
        team1Object={typeof team1Object === 'object' ? team1Object : undefined}
        team2Object={typeof team2Object === 'object' ? team2Object : undefined}
        team1Availability={team1Availability}
        team2Availability={team2Availability}
        tournamentScheduleAvailability={tournamentScheduleAvailability}
      />

      <MatchDeleteConfirmModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        onConfirm={handleDeleteMatch}
        isSubmitting={isSubmitting}
        team1Name={team1Name}
        team2Name={team2Name}
        team1={typeof team1Object === 'object' ? team1Object : undefined}
        team2={typeof team2Object === 'object' ? team2Object : undefined}
      />
    </>
  );
}
